/**
 * CompanySheet — Right-side detail sheet for a company
 *
 * Phase 2 redesign:
 *  - Single "Save All Changes" footer button (replaces per-section saves)
 *  - Dirty-state guard: click-away shows "Discard changes?" dialog
 *  - Suite coupling: Access Financials/Dimensions auto-populates FinOps + Planner
 *  - Test Connection available in read-only mode
 *  - Sync Now (incremental) + Re-sync Full (split button with confirmation)
 *  - Remove integration per row
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  X,
  UserRound,
  Search,
  PoundSterling,
  Users,
  FolderKanban,
  CalendarDays,
  RefreshCw,
  Trash2,
  UserMinus,
  UserPlus,
  ArrowUpRight,
  Link2,
  ChevronDown,
  RotateCcw,
  Save,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { EmployeeImage } from '@/components/EmployeeImage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ApiError, getApiBaseUrl } from '@/services/apiClient';
import { timesheetApi } from '@/services/timesheetApi';
import type { Project, CostCentre } from '@/types/timesheet';
import { ProjectSelector } from '@/components/ProjectSelector';
import { CostCentreSelector } from '@/components/CostCentreSelector';
import * as integrationsService from '@/services/integrationsRedesignService';
import { getApiSecret, saveApiSecret } from '@/services/integrationsRedesignService';
import { getAllUsers as fetchAllUsers } from '@/services/integrationsRedesignService';
import { getExternalInstances, getAccessProjectsCompanies, getAccessPlannerCompanies } from '@/services/integrationsApi';
import type { ExternalInstanceDto, AccessProjectsCompanyDto } from '@/services/integrationsApi';
import {
  FINANCIAL_PRODUCT_LABELS,
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
} from '@/types';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  FinancialProduct,
  HRProduct,
  ProjectProduct,
  PlannerProduct,
  IntegrationConnectionStatus,
  UpdateIntegrationsPayload,
} from '@/types';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Suite coupling constants
// ---------------------------------------------------------------------------

/** Financial products that form a suite requiring FinOps + Planner on same instance */
const SUITE_FINANCIAL_PRODUCTS: FinancialProduct[] = ['access-financials', 'access-dimensions'];

/** Maps financial suite product → required project product */
const SUITE_PROJECT_MAP: Record<string, ProjectProduct> = {
  'access-financials': 'access-financials-operations',
  'access-dimensions': 'access-focalpoint',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanySheetProps {
  company: CompanyWithIntegrations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyUpdate: (updated: CompanyWithIntegrations) => void;
  mode?: 'view' | 'create';
  onCompanyCreated?: (created: CompanyWithIntegrations) => void;
  existingCodes?: string[];
  readOnly?: boolean;
}

/** Local draft state for a single integration row */
interface IntegrationDraft {
  product: string;
  instanceId: string;
  instanceName: string;
  secondaryId: string; // companyId for access-projects/access-planner
  /** Whether this row is locked to the suite (not independently editable) */
  suiteLocked: boolean;
  /** Default project ID for absence entries (HR integration only) */
  defaultProjectId: string;
  /** Default cost centre ID for absence entries (HR integration only) */
  defaultCostCentreId: string;
}

/** All draft state collected at the sheet level */
interface SheetDraft {
  // Company details
  companyName: string;
  companyCode: string;
  isActive: boolean;
  // Integrations
  financial: IntegrationDraft;
  hr: IntegrationDraft;
  project: IntegrationDraft;
  planner: IntegrationDraft;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never';
  return new Date(lastSyncAt).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function ConnectionStatusBadge({ status }: { status: IntegrationConnectionStatus }) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="outline" className="border-success-500 text-success-700 bg-success-50 gap-1 text-xs">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <XCircle className="h-3 w-3" />
          Not Connected
        </Badge>
      );
    case 'not_configured':
      return (
        <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
          Not Configured
        </Badge>
      );
    default:
      return null;
  }
}

function RoleBadge({ role }: { role: string }) {
  const lower = role.toLowerCase();
  let className = 'border-muted-foreground/30 text-muted-foreground';
  if (lower.includes('admin') || lower.includes('manager') || lower.includes('director')) {
    className = 'border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300';
  } else if (lower.includes('lead') || lower.includes('senior')) {
    className = 'border-violet-400 text-violet-700 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300';
  } else if (lower.includes('finance') || lower.includes('account')) {
    className = 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300';
  } else if (lower.includes('hr') || lower.includes('people')) {
    className = 'border-pink-400 text-pink-700 bg-pink-50 dark:bg-pink-950/30 dark:text-pink-300';
  }
  return (
    <Badge variant="outline" className={cn('text-[10px] font-medium px-1.5 py-0 shrink-0', className)}>
      {role}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Integration health summary
// ---------------------------------------------------------------------------

function IntegrationHealthSummary({ company }: { company: CompanyWithIntegrations }) {
  const integrations = [
    { label: 'Financial', status: company.financialIntegration.status },
    { label: 'HR', status: company.hrIntegration.status },
    { label: 'Project', status: company.projectIntegration.status },
    { label: 'Planner', status: company.plannerIntegration.status },
  ];

  const configured = integrations.filter((i) => i.status !== 'not_configured');
  const errors = configured.filter((i) => i.status === 'error');
  const notConnected = configured.filter((i) => i.status === 'not_connected');
  const connected = configured.filter((i) => i.status === 'connected');

  if (configured.length === 0) {
    return (
      <div className="flex items-center gap-2.5 rounded-md bg-muted/50 border px-3 py-2.5 text-sm text-muted-foreground">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>No integrations configured yet.</span>
      </div>
    );
  }
  if (errors.length > 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-md bg-destructive/5 border border-destructive/20 px-3 py-2.5">
        <XCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="text-sm font-medium text-destructive">
            {errors.length} integration{errors.length > 1 ? 's' : ''} {errors.length > 1 ? 'have' : 'has'} an error
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {errors.map((e) => e.label).join(', ')} — update the configuration below
          </p>
        </div>
      </div>
    );
  }
  if (notConnected.length > 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-md bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800 px-3 py-2.5">
        <AlertCircle className="h-4 w-4 shrink-0 text-warning-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
            {notConnected.length} integration{notConnected.length > 1 ? 's' : ''} not connected
          </p>
          <p className="text-xs text-warning-600 dark:text-warning-400 mt-0.5">
            {notConnected.map((e) => e.label).join(', ')} — use Test Connection below to diagnose
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2.5">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" />
      <p className="text-sm font-medium text-success-800 dark:text-success-200">
        {connected.length === configured.length
          ? `All ${connected.length} integration${connected.length > 1 ? 's' : ''} connected`
          : `${connected.length} of ${configured.length} integrations connected`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single integration row — displays config + Test / Sync / Re-sync / Remove
// ---------------------------------------------------------------------------

interface IntegrationRowProps {
  companyId: string;
  type: 'financial' | 'hr' | 'project' | 'planner';
  draft: IntegrationDraft;
  savedStatus: IntegrationConnectionStatus;
  savedLastSyncAt: string | null;
  savedLastFullSyncAt: string | null;
  onDraftChange: (type: 'financial' | 'hr' | 'project' | 'planner', draft: IntegrationDraft) => void;
  onSaved: (updated: CompanyWithIntegrations) => void;
  readOnly: boolean;
}

function IntegrationRow({
  companyId,
  type,
  draft,
  savedStatus,
  savedLastSyncAt,
  savedLastFullSyncAt,
  onDraftChange,
  onSaved,
  readOnly,
}: IntegrationRowProps) {
  const [instances, setInstances] = useState<ExternalInstanceDto[]>([]);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [noInstances, setNoInstances] = useState(false);

  const [projectCompanies, setProjectCompanies] = useState<AccessProjectsCompanyDto[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [noCompanies, setNoCompanies] = useState(false);

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<boolean | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showResyncConfirm, setShowResyncConfirm] = useState(false);
  const [localLastSyncAt, setLocalLastSyncAt] = useState<string | null>(savedLastSyncAt);

  const [maskedApiSecret, setMaskedApiSecret] = useState<string | null>(null);
  const [apiSecretInput, setApiSecretInput] = useState('');
  const [isEditingApiSecret, setIsEditingApiSecret] = useState(false);
  const [isSavingApiSecret, setIsSavingApiSecret] = useState(false);

  // HR default project/cost centre state
  const [hrProjects, setHrProjects] = useState<Project[]>([]);
  const [hrCostCentres, setHrCostCentres] = useState<CostCentre[]>([]);
  const [isLoadingHrProjects, setIsLoadingHrProjects] = useState(false);
  const [isLoadingHrCostCentres, setIsLoadingHrCostCentres] = useState(false);
  const [hrFieldsSubmitted, setHrFieldsSubmitted] = useState(false);

  const instancesLoadedForProduct = useRef<string>('');

  const productOptions =
    type === 'financial'
      ? (Object.entries(FINANCIAL_PRODUCT_LABELS) as [FinancialProduct, string][])
      : type === 'hr'
        ? (Object.entries(HR_PRODUCT_LABELS) as [HRProduct, string][])
        : type === 'project'
          ? (Object.entries(PROJECT_PRODUCT_LABELS) as [ProjectProduct, string][])
          : (Object.entries(PLANNER_PRODUCT_LABELS) as [PlannerProduct, string][]);

  const label =
    type === 'financial' ? 'Financial'
    : type === 'hr' ? 'HR'
    : type === 'project' ? 'Project'
    : 'Planner';

  const icon =
    type === 'financial' ? <PoundSterling className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    : type === 'hr' ? <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    : type === 'project' ? <FolderKanban className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    : <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />;

  const requiresCompanySelection =
    draft.product === 'access-projects' || draft.product === 'access-planner';

  // Load instances whenever product changes
  useEffect(() => {
    if (!draft.product || draft.product === instancesLoadedForProduct.current) return;
    instancesLoadedForProduct.current = draft.product;

    setIsLoadingInstances(true);
    setNoInstances(false);
    setInstances([]);
    getExternalInstances(draft.product, companyId)
      .then((data) => {
        setInstances(data);
        if (data.length === 0) {
          setNoInstances(true);
        } else if (!draft.instanceId) {
          // No instance selected yet — auto-select first available
          onDraftChange(type, {
            ...draft,
            instanceId: data[0].id,
            instanceName: data[0].name,
          });
          if (requiresCompanySelection) loadProjectCompanies(data[0].id, draft.product);
        } else {
          // Existing instance — resolve display name from loaded data
          const match = data.find((i) => i.id === draft.instanceId);
          if (match && match.name !== draft.instanceName) {
            onDraftChange(type, { ...draft, instanceName: match.name });
          }
          if (requiresCompanySelection) loadProjectCompanies(match?.id ?? draft.instanceId, draft.product);
        }
      })
      .catch(() => setNoInstances(true))
      .finally(() => setIsLoadingInstances(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.product]);

  // Load masked API secret whenever the row mounts or the connection status changes.
  // The backend returns { maskedApiSecret: null } for unconfigured integrations.
  useEffect(() => {
    getApiSecret(companyId, type)
      .then((res) => setMaskedApiSecret(res.maskedApiSecret))
      .catch(() => setMaskedApiSecret(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, type, savedStatus]);

  // Sync local last sync when prop changes
  useEffect(() => {
    setLocalLastSyncAt(savedLastSyncAt);
  }, [savedLastSyncAt]);

  // Load projects for HR default project selector
  useEffect(() => {
    if (type !== 'hr' || !draft.product || !draft.instanceId) return;
    setIsLoadingHrProjects(true);
    timesheetApi.getAllProjects(companyId)
      .then((data) => setHrProjects(data))
      .catch(() => setHrProjects([]))
      .finally(() => setIsLoadingHrProjects(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, draft.product, draft.instanceId, companyId]);

  // Load cost centres when default project changes
  useEffect(() => {
    if (type !== 'hr' || !draft.defaultProjectId) {
      setHrCostCentres([]);
      return;
    }
    const project = hrProjects.find((p) => p.id === draft.defaultProjectId);
    const projectCode = project?.code ?? draft.defaultProjectId;
    setIsLoadingHrCostCentres(true);
    timesheetApi.getCostCentresByProjectCode(projectCode)
      .then((data) => setHrCostCentres(data))
      .catch(() => setHrCostCentres([]))
      .finally(() => setIsLoadingHrCostCentres(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, draft.defaultProjectId, hrProjects]);

  async function loadProjectCompanies(instanceId: string, product: string) {
    setIsLoadingCompanies(true);
    setNoCompanies(false);
    setProjectCompanies([]);
    try {
      const data = product === 'access-planner'
        ? await getAccessPlannerCompanies(instanceId)
        : await getAccessProjectsCompanies(instanceId);
      setProjectCompanies(data);
      if (data.length === 0) setNoCompanies(true);
    } catch {
      setNoCompanies(true);
    } finally {
      setIsLoadingCompanies(false);
    }
  }

  function handleProductChange(value: string) {
    instancesLoadedForProduct.current = '';
    onDraftChange(type, {
      product: value,
      instanceId: '',
      instanceName: '',
      secondaryId: '',
      suiteLocked: false,
      defaultProjectId: '',
      defaultCostCentreId: '',
    });
    setInstances([]);
    setNoInstances(false);
    setProjectCompanies([]);
    setNoCompanies(false);
    setTestResult(null);
  }

  function handleInstanceChange(name: string) {
    const instance = instances.find((i) => i.name === name);
    onDraftChange(type, { ...draft, instanceId: instance?.id ?? '', instanceName: name });
    if (requiresCompanySelection && instance) {
      loadProjectCompanies(instance.id, draft.product);
    }
  }

  async function handleSaveApiSecret() {
    if (!apiSecretInput.trim()) return;
    setIsSavingApiSecret(true);
    try {
      const result = await saveApiSecret(companyId, type, apiSecretInput);
      setMaskedApiSecret(result.maskedApiSecret);
      setApiSecretInput('');
      setIsEditingApiSecret(false);
      toast.success('Api Secret saved');
    } catch {
      toast.error('Failed to save Api Secret');
    } finally {
      setIsSavingApiSecret(false);
    }
  }

  // Test connection (available in read-only mode too)
  async function handleTest() {
    setIsTesting(true);
    try {
      const result = await integrationsService.testConnection(companyId, type);
      setTestResult(result.success);
      if (result.success) {
        toast.success('Connection successful', { description: result.message });
      } else {
        toast.error('Connection failed', { description: result.message });
      }
      try {
        const refreshed = await integrationsService.getCompany(companyId);
        if (refreshed) onSaved(refreshed);
      } catch { /* non-fatal */ }
    } catch {
      toast.error('Test connection failed');
      setTestResult(false);
    } finally {
      setIsTesting(false);
    }
  }

  // Incremental sync
  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await integrationsService.syncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Sync complete', { description: result.message });
      } else {
        toast.error('Sync failed', { description: result.message });
      }
    } catch {
      toast.error('Sync failed — please try again');
    } finally {
      setIsSyncing(false);
    }
  }

  // Full re-sync — resets change-tracking state and rebuilds all data
  async function handleResync() {
    setIsResyncing(true);
    setShowResyncConfirm(false);
    try {
      const result = await integrationsService.resyncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Full re-sync complete', { description: result.message });
      } else {
        toast.error('Re-sync failed', { description: result.message });
      }
    } catch {
      toast.error('Re-sync failed — please try again');
    } finally {
      setIsResyncing(false);
    }
  }

  async function handleRemove() {
    setIsRemoving(true);
    try {
      const updated = await integrationsService.removeIntegration(companyId, type);
      if (updated) {
        onSaved(updated);
        onDraftChange(type, { product: '', instanceId: '', instanceName: '', secondaryId: '', suiteLocked: false, defaultProjectId: '', defaultCostCentreId: '' });
        setInstances([]);
        setLocalLastSyncAt(null);
        setTestResult(null);
        instancesLoadedForProduct.current = '';
        toast.success(`${label} integration removed`);
      }
    } catch {
      toast.error('Failed to remove integration');
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirm(false);
    }
  }

  const isConfigured = savedStatus !== 'not_configured';
  const canTestOrSync = !!draft.product && !!draft.instanceId && savedStatus !== 'not_configured';

  return (
    <>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {/* Row header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-semibold">{label} Integration</span>
            {draft.suiteLocked && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 rounded px-1.5 py-0.5 cursor-default">
                      <Link2 className="h-2.5 w-2.5" />
                      Suite
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56 text-xs">
                    This integration shares a database with your Financial integration and uses the same instance automatically.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <ConnectionStatusBadge status={savedStatus} />
        </div>

        {/* Suite locked — show read-only inherited info */}
        {draft.suiteLocked ? (
          <div className="space-y-2">
            <div className="rounded-md bg-muted/50 border border-dashed px-3 py-2.5 space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Product</p>
              <p className="text-sm">
                {productOptions.find(([v]) => v === draft.product)?.[1] ?? draft.product}
              </p>

            </div>
            {draft.instanceName && (
              <div className="rounded-md bg-muted/50 border border-dashed px-3 py-2.5 space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Instance</p>
                <p className="text-sm">{draft.instanceName}</p>
              </div>
            )}
            {/* Test + Sync available for suite-locked rows */}
            <div className="flex items-center gap-2 pt-1">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={!canTestOrSync ? 0 : -1}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={handleTest}
                        disabled={isTesting || !canTestOrSync}
                      >
                        {isTesting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Test Connection
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canTestOrSync && (
                    <TooltipContent side="bottom" className="text-xs max-w-48 text-center">
                      Save the integration first to test the connection
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              {testResult !== null && (
                <span className={cn('text-xs font-medium flex items-center gap-1', testResult ? 'text-success-600' : 'text-destructive')}>
                  {testResult ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                  {testResult ? 'Pass' : 'Failed'}
                </span>
              )}
              {/* Sync Now + Re-sync — when connected */}
              {savedStatus === 'connected' && (
                <div className="flex items-stretch gap-0 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 rounded-r-none border-r-0"
                    onClick={handleSync}
                    disabled={isSyncing || isResyncing}
                  >
                    {isSyncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    {isSyncing ? 'Syncing…' : 'Sync Now'}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 rounded-l-none px-1.5"
                        disabled={isSyncing || isResyncing}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        className="text-xs gap-2 text-muted-foreground"
                        onClick={() => setShowResyncConfirm(true)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Full Re-sync…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Normal (non-locked) integration row */
          <div className="space-y-3">
            {/* Product */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Product</Label>
              {readOnly ? (
                <p className="text-sm py-1">
                  {draft.product
                    ? (productOptions.find(([v]) => v === draft.product)?.[1] ?? draft.product)
                    : <span className="italic text-muted-foreground">Not configured</span>}
                </p>
              ) : (
                <Select value={draft.product} onValueChange={handleProductChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select product…" />
                  </SelectTrigger>
                  <SelectContent>
                    {productOptions.map(([value, lbl]) => (
                      <SelectItem key={value} value={value}>{lbl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Instance Name (non-company-selection products) */}
            {draft.product && !requiresCompanySelection && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Instance</Label>
                {readOnly ? (
                  <p className="text-sm py-1">
                    {draft.instanceName || <span className="italic text-muted-foreground">—</span>}
                  </p>
                ) : isLoadingInstances ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading instances…
                  </div>
                ) : noInstances ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>No instances found for this product.</AlertDescription>
                  </Alert>
                ) : (
                  <Select value={draft.instanceName} onValueChange={handleInstanceChange}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select instance…" />
                    </SelectTrigger>
                    <SelectContent>
                      {instances.map((i) => (
                        <SelectItem key={i.id} value={i.name} disabled={!!i.reservedByCompanyId}>
                          {i.reservedByCompanyId
                            ? `${i.name} — in use by ${i.reservedByCompanyName}`
                            : i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Webhook URL for People HR planner */}
            {draft.product === 'people-hr' && draft.instanceId && !isLoadingInstances && !noInstances && (() => {
              const base = getApiBaseUrl();
              const baseWithApi = base.endsWith('/api') ? base : `${base}/api`;
              const organisationId = sessionStorage.getItem('evo_organisation_id') || '';
              const webhookUrl = `${baseWithApi}/peoplehr/webhook/${organisationId}`;
              return (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Webhook URL</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={webhookUrl}
                      className="h-8 text-sm bg-muted cursor-default"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 flex-shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(webhookUrl);
                        toast.success('Webhook URL copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* Api Secret field — shown only for People HR */}
            {draft.product === 'people-hr' && draft.instanceId && !isLoadingInstances && !noInstances && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Api Secret</Label>
                {readOnly ? (
                  <p className="text-sm py-1 font-mono">
                    {maskedApiSecret ?? <span className="italic text-muted-foreground">—</span>}
                  </p>
                ) : isEditingApiSecret ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      value={apiSecretInput}
                      onChange={(e) => setApiSecretInput(e.target.value)}
                      placeholder="Enter Api Secret"
                      className="h-8 text-sm"
                    />
                    <Button
                      variant="default"
                      size="sm"
                      className="h-8 px-3 flex-shrink-0"
                      onClick={handleSaveApiSecret}
                      disabled={isSavingApiSecret || !apiSecretInput.trim()}
                    >
                      {isSavingApiSecret ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 flex-shrink-0"
                      onClick={() => { setIsEditingApiSecret(false); setApiSecretInput(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={maskedApiSecret ?? ''}
                      placeholder="Not set"
                      className="h-8 text-sm bg-muted cursor-default font-mono"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 flex-shrink-0"
                      onClick={() => setIsEditingApiSecret(true)}
                    >
                      {maskedApiSecret ? 'Update' : 'Set'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Default Project and Cost Centre selectors for HR integration */}
            {type === 'hr' && draft.product && draft.instanceId && !isLoadingInstances && !noInstances && (() => {
              return (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Project</Label>
                    {readOnly ? (
                      <p className="text-sm py-1">
                        {draft.defaultProjectId
                          ? (hrProjects.find((p) => p.id === draft.defaultProjectId)?.name ?? draft.defaultProjectId)
                          : <span className="italic text-muted-foreground">—</span>}
                      </p>
                    ) : isLoadingHrProjects ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading projects…
                      </div>
                    ) : (
                      <ProjectSelector
                        projects={hrProjects}
                        value={draft.defaultProjectId || undefined}
                        onChange={(projectId) => {
                          onDraftChange(type, { ...draft, defaultProjectId: projectId, defaultCostCentreId: '' });
                        }}
                        placeholder="Select project…"
                        showFavourites={false}
                        required
                        isSubmitted={hrFieldsSubmitted}
                        companyId={companyId}
                      />
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Cost Centre</Label>
                    {readOnly ? (
                      <p className="text-sm py-1">
                        {draft.defaultCostCentreId
                          ? (hrCostCentres.find((c) => c.id === draft.defaultCostCentreId)?.name ?? draft.defaultCostCentreId)
                          : <span className="italic text-muted-foreground">—</span>}
                      </p>
                    ) : isLoadingHrCostCentres ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading cost centres…
                      </div>
                    ) : (
                      <CostCentreSelector
                        costCentres={hrCostCentres}
                        value={draft.defaultCostCentreId || undefined}
                        onChange={(costCentreId) => {
                          onDraftChange(type, { ...draft, defaultCostCentreId: costCentreId });
                        }}
                        placeholder="Select cost centre…"
                        showFavourites={false}
                        required
                        isSubmitted={hrFieldsSubmitted}
                      />
                    )}
                  </div>
                </>
              );
            })()}

            {/* Company selector for Access Projects / Access Planner */}
            {draft.product && requiresCompanySelection && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Company</Label>
                {readOnly ? (
                  <p className="text-sm py-1">
                    {draft.secondaryId
                      ? (projectCompanies.find((c) => c.id === draft.secondaryId)?.name ?? draft.secondaryId)
                      : <span className="italic text-muted-foreground">—</span>}
                  </p>
                ) : isLoadingInstances || isLoadingCompanies ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading companies…
                  </div>
                ) : noInstances ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>No instances found for this product.</AlertDescription>
                  </Alert>
                ) : noCompanies ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>No companies found in this instance.</AlertDescription>
                  </Alert>
                ) : (
                  <Select
                    value={draft.secondaryId}
                    onValueChange={(v) => onDraftChange(type, { ...draft, secondaryId: v })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select company…" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectCompanies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Last sync */}
            {localLastSyncAt && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <RefreshCw className="h-3 w-3" />
                Last synced: <span className="font-medium text-foreground">{formatLastSync(localLastSyncAt)}</span>
              </div>
            )}
            {savedLastFullSyncAt && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <RotateCcw className="h-3 w-3" />
                Last full sync: <span className="font-medium text-foreground">{formatLastSync(savedLastFullSyncAt)}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {/* Test Connection — always visible, even in readOnly */}
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={!canTestOrSync ? 0 : -1}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={handleTest}
                        disabled={isTesting || !canTestOrSync}
                      >
                        {isTesting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Test Connection
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canTestOrSync && (
                    <TooltipContent side="bottom" className="text-xs max-w-48 text-center">
                      {!draft.product ? 'Select a product first' : !draft.instanceId ? 'Select an instance first' : 'Save the integration first to test the connection'}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>

              {/* Test result inline */}
              {testResult !== null && (
                <span className={cn('text-xs font-medium flex items-center gap-1', testResult ? 'text-success-600' : 'text-destructive')}>
                  {testResult ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                  {testResult ? 'Pass' : 'Failed'}
                </span>
              )}

              {/* Sync Now + Re-sync — only when connected and not read-only */}
              {!readOnly && savedStatus === 'connected' && (
                <div className="flex items-stretch gap-0 ml-auto">
                  {/* Sync Now primary */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 rounded-r-none border-r-0"
                    onClick={handleSync}
                    disabled={isSyncing || isResyncing}
                  >
                    {isSyncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    {isSyncing ? 'Syncing…' : 'Sync Now'}
                  </Button>
                  {/* Dropdown for Re-sync */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 rounded-l-none px-1.5"
                        disabled={isSyncing || isResyncing}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        className="text-xs gap-2 text-muted-foreground"
                        onClick={() => setShowResyncConfirm(true)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Full Re-sync…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              {/* Remove — only when configured and not read-only */}
              {!readOnly && isConfigured && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowRemoveConfirm(true)}
                  disabled={isRemoving}
                >
                  {isRemoving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Remove
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Remove confirm */}
      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {label} Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect and remove all configuration for the {label.toLowerCase()} integration.
              Any active sync schedule will be cancelled. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Removing…</> : 'Remove Integration'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-sync confirm */}
      <AlertDialog open={showResyncConfirm} onOpenChange={setShowResyncConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning-600" />
              Full Re-sync {label} Integration?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  A full re-sync rebuilds all data from scratch. This is different from a standard sync which only picks up recent changes.
                </p>
                <ul className="list-disc pl-4 space-y-1 text-xs">
                  <li>May take several minutes to complete</li>
                  <li>Places higher load on the connected database</li>
                  <li>All incremental changes since the last sync will be reprocessed</li>
                </ul>
                <p className="text-xs font-medium text-foreground">
                  Only use this if you suspect data is out of sync. For routine updates, use Sync Now instead.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResync} className="bg-warning-600 hover:bg-warning-700 text-white">
              {isResyncing ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Re-syncing…</> : 'Start Full Re-sync'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Add Users Dialog
// ---------------------------------------------------------------------------

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyWithIntegrations;
  onUsersAdded: () => void;
}

function AddUsersDialog({ open, onOpenChange, company, onUsersAdded }: AddUsersDialogProps) {
  const [allUsers, setAllUsers] = useState<CompanyUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDialogSearch('');
    setSelectedIds(new Set());
    setIsLoadingUsers(true);
    fetchAllUsers(company.id)
      .then(setAllUsers)
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setIsLoadingUsers(false));
  }, [open, company.id]);

  const filteredDialogUsers = dialogSearch.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.role.toLowerCase().includes(dialogSearch.toLowerCase())
      )
    : allUsers;

  const allFilteredSelected = filteredDialogUsers.length > 0 && filteredDialogUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected = filteredDialogUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) { filteredDialogUsers.forEach((u) => next.delete(u.id)); }
      else { filteredDialogUsers.forEach((u) => next.add(u.id)); }
      return next;
    });
  }

  async function handleConfirmAdd() {
    if (selectedIds.size === 0) return;
    setIsAdding(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => integrationsService.addUserToCompany(company.id, id)));
      toast.success(`${ids.length} user${ids.length !== 1 ? 's' : ''} added to ${company.name}`);
      onUsersAdded();
      onOpenChange(false);
    } catch {
      toast.error('Failed to add some users');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Add Users to {company.name}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Select one or more users to grant access to this company.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input className="pl-8 h-8 text-sm" placeholder="Search by name, email, role…" value={dialogSearch} onChange={(e) => setDialogSearch(e.target.value)} autoFocus />
          </div>
        </div>
        {!isLoadingUsers && filteredDialogUsers.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors" onClick={toggleAllFiltered}>
            <Checkbox checked={allFilteredSelected} data-state={someFilteredSelected ? 'indeterminate' : undefined} onCheckedChange={toggleAllFiltered} className="pointer-events-none" />
            <span className="text-xs font-medium text-muted-foreground">
              {allFilteredSelected ? 'Deselect all' : someFilteredSelected ? `${selectedIds.size} selected — select all ${filteredDialogUsers.length}` : `Select all ${filteredDialogUsers.length} user${filteredDialogUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingUsers ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1 flex-1"><Skeleton className="h-3 w-32" /><Skeleton className="h-3 w-20" /></div>
                </div>
              ))}
            </div>
          ) : filteredDialogUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
              {dialogSearch ? (
                <><Search className="h-7 w-7 text-muted-foreground/40" /><p className="text-sm text-muted-foreground">No users match "{dialogSearch}"</p></>
              ) : (
                <><Users className="h-7 w-7 text-muted-foreground/40" /><p className="text-sm text-muted-foreground">All users already have access</p></>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredDialogUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                return (
                  <div key={user.id} className={cn('flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors', isSelected ? 'bg-primary/5' : 'hover:bg-muted/50')} onClick={() => toggleUser(user.id)}>
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleUser(user.id)} className="pointer-events-none shrink-0" />
                    <EmployeeImage name={user.name} photoUrl={user.photoUrl} size="sm" className="flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{user.role}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isAdding}>Cancel</Button>
          <Button size="sm" onClick={handleConfirmAdd} disabled={selectedIds.size === 0 || isAdding} className="gap-1.5">
            {isAdding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            {selectedIds.size > 0 ? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? 's' : ''}` : 'Add Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk Remove Bar
// ---------------------------------------------------------------------------

function BulkRemoveBar({ selectedCount, onRemove, onClear, isRemoving }: { selectedCount: number; onRemove: () => void; onClear: () => void; isRemoving: boolean }) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
      <div className="flex justify-center pb-2">
        <div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a7f6e] text-white shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">{selectedCount}</span>
          <span className="font-medium pr-1">{selectedCount === 1 ? 'user' : 'users'} selected</span>
          <div className="h-4 w-px bg-white/30" />
          <button onClick={onRemove} disabled={isRemoving} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60">
            {isRemoving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
            Remove Access
          </button>
          <div className="h-4 w-px bg-white/30" />
          <button onClick={onClear} className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1" aria-label="Clear selection">
            <X className="h-3.5 w-3.5" /><span className="text-xs">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users with Access section
// ---------------------------------------------------------------------------

function UsersWithAccessSection({ company, onUserCountChange }: { company: CompanyWithIntegrations; onUserCountChange: (count: number) => void }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterQuery, setFilterQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationsService.getCompanyUsers(company.id);
      setUsers(data);
      onUserCountChange(data.length);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    loadUsers();
    setSelectedIds(new Set());
  }, [loadUsers]);

  const filteredUsers = filterQuery.trim()
    ? users.filter((u) => u.name.toLowerCase().includes(filterQuery.toLowerCase()) || u.role.toLowerCase().includes(filterQuery.toLowerCase()))
    : users;

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected = filteredUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) { setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); }
  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) { filteredUsers.forEach((u) => next.delete(u.id)); }
      else { filteredUsers.forEach((u) => next.add(u.id)); }
      return next;
    });
  }

  async function handleBulkRemove() {
    setIsBulkRemoving(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => integrationsService.removeUserFromCompany(company.id, id)));
      toast.success(`Removed ${ids.length} user${ids.length !== 1 ? 's' : ''} from ${company.name}`);
      setSelectedIds(new Set());
      setShowBulkConfirm(false);
      await loadUsers();
    } catch {
      toast.error('Failed to remove some users');
    } finally {
      setIsBulkRemoving(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input className="pl-8 h-8 text-sm" placeholder="Filter users…" value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1 shrink-0" onClick={() => setIsAddDialogOpen(true)}>
          <UserPlus className="h-3 w-3" /> Add Users
        </Button>
      </div>

      {!isLoading && filteredUsers.length > 0 && (
        <div className="flex items-center gap-3 px-2 py-1.5 rounded-md bg-muted/40 border cursor-pointer hover:bg-muted/60 transition-colors" onClick={toggleAllFiltered}>
          <Checkbox checked={allFilteredSelected} data-state={someFilteredSelected ? 'indeterminate' : undefined} onCheckedChange={toggleAllFiltered} className="pointer-events-none" />
          <span className="text-xs text-muted-foreground font-medium flex-1">
            {allFilteredSelected ? 'Deselect all' : someFilteredSelected ? `${selectedIds.size} of ${filteredUsers.length} selected` : filterQuery.trim() ? `Select all ${filteredUsers.length} matching` : `Select all ${filteredUsers.length} users`}
          </span>
          {!isLoading && <span className="text-xs text-muted-foreground">{filterQuery.trim() ? `${filteredUsers.length} of ${users.length}` : `${users.length} user${users.length !== 1 ? 's' : ''}`}</span>}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 py-1 px-2">
              <Skeleton className="h-4 w-4 rounded" /><Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1 flex-1"><Skeleton className="h-3 w-28" /><Skeleton className="h-3 w-16" /></div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <UserRound className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users assigned yet.</p>
          <Button variant="outline" size="sm" className="mt-1 gap-1.5 text-xs" onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" /> Add Users
          </Button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
          <Search className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users match "{filterQuery}"</p>
        </div>
      ) : (
        <div className="divide-y rounded-md border overflow-hidden">
          {filteredUsers.map((user) => {
            const isSelected = selectedIds.has(user.id);
            return (
              <div key={user.id} className={cn('flex items-center gap-3 py-2.5 px-3 transition-colors cursor-pointer group', isSelected ? 'bg-primary/5' : 'hover:bg-muted/40')} onClick={() => toggleUser(user.id)}>
                <Checkbox checked={isSelected} onCheckedChange={() => toggleUser(user.id)} className="pointer-events-none shrink-0" />
                <EmployeeImage name={user.name} photoUrl={user.photoUrl} size="sm" className="flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email ?? user.role}</div>
                </div>
                <RoleBadge role={user.role} />
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && (
        <div className="flex items-center justify-between pt-1 border-t gap-2">
          <p className="text-xs text-muted-foreground">To manage a user's full profile, go to Team Members.</p>
          <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 shrink-0 text-muted-foreground hover:text-foreground" onClick={() => navigate('/team/members')}>
            Team Members <ArrowUpRight className="h-3 w-3" />
          </Button>
        </div>
      )}

      <BulkRemoveBar selectedCount={selectedIds.size} onRemove={() => setShowBulkConfirm(true)} onClear={() => setSelectedIds(new Set())} isRemoving={isBulkRemoving} />

      <AddUsersDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} company={company} onUsersAdded={loadUsers} />

      <AlertDialog open={showBulkConfirm} onOpenChange={(o) => { if (!o) setShowBulkConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIds.size === 1 ? 'This user will lose access to ' : `These ${selectedIds.size} users will lose access to `}
              <strong>{company.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkRemove} disabled={isBulkRemoving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isBulkRemoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Company Form
// ---------------------------------------------------------------------------

function CreateCompanyForm({ existingCodes, onCreated, onCancel }: { existingCodes: string[]; onCreated: (company: CompanyWithIntegrations) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  function validate() {
    const e: { name?: string; code?: string } = {};
    if (!name.trim() || name.trim().length < 2) e.name = 'Company name must be at least 2 characters';
    if (!code.trim() || code.trim().length < 2) e.code = 'Company code must be at least 2 characters';
    else if (code.trim().length > 20) e.code = 'Company code must be at most 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code.trim())) e.code = 'Uppercase letters, numbers, and hyphens only';
    else if (existingCodes.includes(code.trim().toUpperCase())) e.code = 'This company code is already in use';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setIsSaving(true);
    try {
      const created = await integrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status });
      toast.success(`${created.name} created`);
      onCreated(created);
    } catch {
      toast.error('Failed to create company');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="new-company-name">Company Name <span className="text-destructive">*</span></Label>
        <Input id="new-company-name" value={name} placeholder="e.g., UK Operations" onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} disabled={isSaving} />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new-company-code">Company Code <span className="text-destructive">*</span></Label>
        <Input id="new-company-code" value={code} placeholder="e.g., UK-OPS" className="uppercase" onChange={(e) => { setCode(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, code: undefined })); }} disabled={isSaving} />
        <p className="text-xs text-muted-foreground">Unique identifier — uppercase letters, numbers and hyphens only</p>
        {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
      </div>
      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="new-company-status" className="text-sm font-medium">Active</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
        </div>
        <Switch id="new-company-status" checked={status === 'active'} onCheckedChange={(checked) => setStatus(checked ? 'active' : 'inactive')} disabled={isSaving} />
      </div>
      <Separator />
      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isSaving}>Cancel</Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Create Company
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CompanySheet
// ---------------------------------------------------------------------------

function buildEmptyDraft(): IntegrationDraft {
  return { product: '', instanceId: '', instanceName: '', secondaryId: '', suiteLocked: false, defaultProjectId: '', defaultCostCentreId: '' };
}

function buildDraftFromCompany(company: CompanyWithIntegrations): SheetDraft {
  const financialProduct = company.financialIntegration.product ?? '';
  const isSuiteFinancial = SUITE_FINANCIAL_PRODUCTS.includes(financialProduct as FinancialProduct);
  const suiteProjectProduct = isSuiteFinancial ? SUITE_PROJECT_MAP[financialProduct] : null;

  return {
    companyName: company.name,
    companyCode: company.code,
    isActive: company.status === 'active',
    financial: {
      product: financialProduct,
      instanceId: company.financialIntegration.externalInstanceId ?? '',
      instanceName: company.financialIntegration.instanceName ?? '',
      secondaryId: '',
      suiteLocked: false,
      defaultProjectId: '',
      defaultCostCentreId: '',
    },
    hr: {
      product: company.hrIntegration.product ?? '',
      instanceId: company.hrIntegration.externalInstanceId ?? '',
      instanceName: company.hrIntegration.instanceName ?? '',
      secondaryId: '',
      suiteLocked: false,
      defaultProjectId: company.hrIntegration.defaultProjectId ?? '',
      defaultCostCentreId: company.hrIntegration.defaultCostCentreId ?? '',
    },
    project: {
      product: company.projectIntegration.product ?? '',
      instanceId: company.projectIntegration.externalInstanceId ?? '',
      instanceName: company.projectIntegration.instanceName ?? '',
      secondaryId: company.projectIntegration.externalInstanceIdSecondary ?? '',
      // Lock if the project product matches the expected suite product
      suiteLocked: isSuiteFinancial && !!suiteProjectProduct && company.projectIntegration.product === suiteProjectProduct,
      defaultProjectId: '',
      defaultCostCentreId: '',
    },
    planner: {
      product: company.plannerIntegration.product ?? '',
      instanceId: company.plannerIntegration.externalInstanceId ?? '',
      instanceName: company.plannerIntegration.instanceName ?? '',
      secondaryId: company.plannerIntegration.externalInstanceIdSecondary ?? '',
      // Planner can be suite-locked if it's on same instance as financial suite
      suiteLocked: isSuiteFinancial && (
        company.plannerIntegration.product === 'access-resource-scheduler' ||
        company.plannerIntegration.product === 'access-planner'
      ) && company.plannerIntegration.externalInstanceId === company.financialIntegration.externalInstanceId,
      defaultProjectId: '',
      defaultCostCentreId: '',
    },
  };
}

export function CompanySheet({
  company,
  open,
  onOpenChange,
  onCompanyUpdate,
  mode = 'view',
  onCompanyCreated,
  existingCodes = [],
  readOnly = false,
}: CompanySheetProps) {
  const [draft, setDraft] = useState<SheetDraft | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [activeCompany, setActiveCompany] = useState<CompanyWithIntegrations | null>(company);

  // Track which suite project products are actually provisioned for the org
  const suiteProductsAvailableRef = useRef<Record<string, boolean>>({});

  // Check suite project product availability via Workspace API
  const checkSuiteProductAvailability = useCallback(async (companyId: string) => {
    const availability: Record<string, boolean> = {};
    const products = Object.values(SUITE_PROJECT_MAP);
    await Promise.all(
      products.map(async (product) => {
        try {
          const instances = await getExternalInstances(product, companyId);
          availability[product] = instances.length > 0;
        } catch {
          availability[product] = false;
        }
      })
    );
    suiteProductsAvailableRef.current = availability;
    return availability;
  }, []);

  // Initialise / reset draft when company or open state changes
  useEffect(() => {
    if (open && company) {
      setActiveCompany(company);
      setIsDirty(false);

      // Build initial draft, then refine suiteLocked once availability is known
      const initialDraft = buildDraftFromCompany(company);
      setDraft(initialDraft);

      checkSuiteProductAvailability(company.id).then((availability) => {
        setDraft((prev) => {
          if (!prev) return prev;
          const financialProduct = prev.financial.product;
          const isSuiteFinancial = SUITE_FINANCIAL_PRODUCTS.includes(financialProduct as FinancialProduct);
          const suiteProjectProduct = isSuiteFinancial ? SUITE_PROJECT_MAP[financialProduct] : null;

          // If the suite project product is not provisioned, unlock the project row
          if (suiteProjectProduct && !availability[suiteProjectProduct]) {
            return { ...prev, project: { ...prev.project, suiteLocked: false } };
          }
          return prev;
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id]);

  // Keep activeCompany in sync when parent updates it (e.g. after a test connection)
  useEffect(() => {
    if (company) setActiveCompany(company);
  }, [company]);

  // Intercept sheet close — show discard dialog if dirty
  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onOpenChange(nextOpen);
  }

  function handleDiscard() {
    setShowDiscardDialog(false);
    setIsDirty(false);
    onOpenChange(false);
  }

  function updateDraftField(key: keyof SheetDraft, value: unknown) {
    setDraft((prev) => prev ? { ...prev, [key]: value } : prev);
    setIsDirty(true);
  }

  function handleIntegrationDraftChange(
    type: 'financial' | 'hr' | 'project' | 'planner',
    newDraft: IntegrationDraft
  ) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [type]: newDraft };

      // Suite coupling: when financial product changes to/from a suite product,
      // auto-update project and planner to match
      if (type === 'financial') {
        const isSuite = SUITE_FINANCIAL_PRODUCTS.includes(newDraft.product as FinancialProduct);
        const suiteProjectProduct = isSuite ? SUITE_PROJECT_MAP[newDraft.product] : null;

        if (isSuite && suiteProjectProduct) {
          // Only lock if the suite project product is provisioned for the org
          const isProjectProductAvailable = suiteProductsAvailableRef.current[suiteProjectProduct] !== false;

          if (isProjectProductAvailable) {
            // Lock project to suite product — leave instanceId/instanceName empty;
            // the backend will resolve the correct related instance via DatasetId lookup.
            next.project = {
              product: suiteProjectProduct,
              instanceId: '',
              instanceName: '',
              secondaryId: prev.project.secondaryId,
              suiteLocked: true,
              defaultProjectId: '',
              defaultCostCentreId: '',
            };
          }
          // If suite project product is NOT provisioned, leave project unlocked
          // so the user can select access-projects or another available product.

          // Lock planner if it was already on a planner product — leave instance
          // empty for backend auto-population.
          const plannerProduct = prev.planner.product;
          if (plannerProduct === 'access-resource-scheduler' || plannerProduct === 'access-planner') {
            next.planner = {
              ...prev.planner,
              instanceId: '',
              instanceName: '',
              suiteLocked: true,
            };
          }
        } else {
          // Financial changed away from suite — unlock dependents
          next.project = { ...prev.project, suiteLocked: false };
          next.planner = { ...prev.planner, suiteLocked: false };
        }
      }

      return next;
    });
    setIsDirty(true);
  }

  // Single save — commits all changes at once
  async function handleSaveAll() {
    if (!draft || !activeCompany) return;

    // Validate HR default project/cost centre
    if (draft.hr.product && draft.hr.instanceId) {
      if (!draft.hr.defaultProjectId || !draft.hr.defaultCostCentreId) {
        toast.error('HR integration requires a default Project and Cost Centre');
        return;
      }
    }

    setIsSaving(true);
    try {
      // 1. Save company details if changed
      const detailsChanged =
        draft.companyName !== activeCompany.name ||
        draft.companyCode !== activeCompany.code ||
        (draft.isActive ? 'active' : 'inactive') !== activeCompany.status;

      if (detailsChanged) {
        const updatedCompany = await integrationsService.updateCompany(activeCompany.id, {
          name: draft.companyName,
          code: draft.companyCode.toUpperCase(),
          status: draft.isActive ? 'active' : 'inactive',
        });
        if (updatedCompany) {
          setActiveCompany(updatedCompany);
          onCompanyUpdate(updatedCompany);
        }
      }

      // 2. Save integrations
      const payload: UpdateIntegrationsPayload = {
        financial: {
          product: (draft.financial.product || null) as FinancialProduct | null,
          externalInstanceId: draft.financial.instanceId || null,
          instanceName: draft.financial.instanceName || null,
        },
        hr: {
          product: (draft.hr.product || null) as HRProduct | null,
          externalInstanceId: draft.hr.instanceId || null,
          instanceName: draft.hr.instanceName || null,
          defaultProjectId: draft.hr.defaultProjectId || null,
          defaultCostCentreId: draft.hr.defaultCostCentreId || null,
        },
        project: {
          product: (draft.project.product || null) as ProjectProduct | null,
          externalInstanceId: draft.project.instanceId || null,
          instanceName: draft.project.instanceName || null,
          externalInstanceIdSecondary: draft.project.secondaryId || null,
        },
        planner: {
          product: (draft.planner.product || null) as PlannerProduct | null,
          externalInstanceId: draft.planner.instanceId || null,
          instanceName: draft.planner.instanceName || null,
          externalInstanceIdSecondary: draft.planner.secondaryId || null,
        },
      };

      const updated = await integrationsService.updateIntegrations(activeCompany.id, payload);
      if (updated) {
        setActiveCompany(updated);
        onCompanyUpdate(updated);
        setDraft(buildDraftFromCompany(updated));
        setIsDirty(false);
        toast.success('Changes saved');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.data?.conflicts) {
        const conflicts = err.data.conflicts as Array<{
          integrationType: string;
          externalInstanceId: string;
          reservedByCompanyId: string;
          reservedByCompanyName: string;
        }>;
        conflicts.forEach((c) => {
          toast.error('Instance already in use', {
            description: `The ${c.integrationType} instance is reserved by "${c.reservedByCompanyName}"`,
          });
        });
      } else {
        toast.error('Failed to save changes — please try again');
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleUserCountChange(count: number) {
    if (!activeCompany) return;
    const updated = { ...activeCompany, userCount: count };
    setActiveCompany(updated);
    onCompanyUpdate(updated);
  }

  // Handle live updates from test/sync operations within rows (no dirty state change)
  function handleRowSaved(updated: CompanyWithIntegrations) {
    setActiveCompany(updated);
    onCompanyUpdate(updated);
    // Rebuild draft from updated company but preserve user edits if dirty
    if (!isDirty) {
      setDraft(buildDraftFromCompany(updated));
    }
  }

  const isSuiteFinancial = draft
    ? SUITE_FINANCIAL_PRODUCTS.includes(draft.financial.product as FinancialProduct)
    : false;

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden"
          // Prevent accidental outside-click close when dirty — handled by handleOpenChange
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-base leading-tight truncate">
                  {mode === 'create' ? 'Add New Company' : (activeCompany?.name ?? 'Company Details')}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {mode === 'create' ? 'Fill in the details below to create a new company' : activeCompany?.code}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {mode === 'create' ? (
            <CreateCompanyForm
              existingCodes={existingCodes}
              onCreated={(created) => { onCompanyCreated?.(created); onOpenChange(false); }}
              onCancel={() => onOpenChange(false)}
            />
          ) : !activeCompany || !draft ? null : (
            <Tabs defaultValue="integrations" className="flex flex-col flex-1 min-h-0">
              {/* Tab bar */}
              <div className="px-6 pt-3 pb-0 border-b shrink-0">
                <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                  <TabsTrigger value="integrations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm">
                    Integrations
                  </TabsTrigger>
                  <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm">
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm">
                    Users
                    <span className={cn('ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-medium h-4 min-w-4 px-1', activeCompany.userCount === 0 ? 'bg-muted/60 text-muted-foreground/60' : 'bg-muted text-muted-foreground')}>
                      {activeCompany.userCount}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Dirty state warning banner */}
              {isDirty && (
                <div className="flex items-center gap-2 px-6 py-2 bg-warning-50 dark:bg-warning-950/30 border-b border-warning-200 dark:border-warning-800 shrink-0">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning-600 shrink-0" />
                  <p className="text-xs text-warning-700 dark:text-warning-300 flex-1">
                    You have unsaved changes. Use <strong>Save All Changes</strong> below to commit them.
                  </p>
                </div>
              )}

              {/* ── Tab: Integrations ── */}
              <TabsContent value="integrations" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 py-5 space-y-4 pb-8">
                  {/* Health summary — reflects saved state */}
                  <IntegrationHealthSummary company={activeCompany} />

                  {/* ── Access Suite group: Financial + Project + Planner ── */}
                  <div className={cn(
                    'rounded-lg border',
                    isSuiteFinancial
                      ? 'border-primary/30 bg-primary/[0.03]'
                      : 'border-border bg-muted/20'
                  )}>
                    {/* Group header */}
                    <div className={cn(
                      'flex items-center gap-2 px-4 py-2.5 border-b',
                      isSuiteFinancial
                        ? 'border-primary/20 bg-primary/[0.05] rounded-t-lg'
                        : 'border-border rounded-t-lg'
                    )}>
                      {isSuiteFinancial ? (
                        <>
                          <Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span className="text-xs font-semibold text-primary tracking-wide uppercase">
                            Access Suite
                          </span>
                          <Badge variant="outline" className="ml-1 border-primary/40 text-primary bg-primary/5 text-[10px] px-1.5 py-0 h-4">
                            Active
                          </Badge>
                        </>
                      ) : (
                        <>
                          <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                            Financial, Project &amp; Planner
                          </span>
                        </>
                      )}
                    </div>

                    {/* Rows inside group */}
                    <div className="divide-y divide-border">
                      <div className="px-4 py-4">
                        <IntegrationRow
                          companyId={activeCompany.id}
                          type="financial"
                          draft={draft.financial}
                          savedStatus={activeCompany.financialIntegration.status}
                          savedLastSyncAt={activeCompany.financialIntegration.lastSyncAt}
                          savedLastFullSyncAt={activeCompany.financialIntegration.lastFullSyncAt}
                          onDraftChange={handleIntegrationDraftChange}
                          onSaved={handleRowSaved}
                          readOnly={readOnly}
                        />
                      </div>
                      <div className="px-4 py-4">
                        <IntegrationRow
                          companyId={activeCompany.id}
                          type="project"
                          draft={draft.project}
                          savedStatus={activeCompany.projectIntegration.status}
                          savedLastSyncAt={activeCompany.projectIntegration.lastSyncAt}
                          savedLastFullSyncAt={activeCompany.projectIntegration.lastFullSyncAt}
                          onDraftChange={handleIntegrationDraftChange}
                          onSaved={handleRowSaved}
                          readOnly={readOnly}
                        />
                      </div>
                      <div className="px-4 py-4">
                        <IntegrationRow
                          companyId={activeCompany.id}
                          type="planner"
                          draft={draft.planner}
                          savedStatus={activeCompany.plannerIntegration.status}
                          savedLastSyncAt={activeCompany.plannerIntegration.lastSyncAt}
                          savedLastFullSyncAt={activeCompany.plannerIntegration.lastFullSyncAt}
                          onDraftChange={handleIntegrationDraftChange}
                          onSaved={handleRowSaved}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── HR group: standalone ── */}
                  <div className="rounded-lg border border-border bg-muted/20">
                    {/* Group header */}
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border rounded-t-lg">
                      <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                        HR
                      </span>
                    </div>

                    {/* Row inside group */}
                    <div className="px-4 py-4">
                      <IntegrationRow
                        companyId={activeCompany.id}
                        type="hr"
                        draft={draft.hr}
                        savedStatus={activeCompany.hrIntegration.status}
                        savedLastSyncAt={activeCompany.hrIntegration.lastSyncAt}
                        savedLastFullSyncAt={activeCompany.hrIntegration.lastFullSyncAt}
                        onDraftChange={handleIntegrationDraftChange}
                        onSaved={handleRowSaved}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ── Tab: Details ── */}
              <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 py-5 space-y-5">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="company-name">Company Name</Label>
                      <Input
                        id="company-name"
                        value={draft.companyName}
                        onChange={(e) => { if (!readOnly) updateDraftField('companyName', e.target.value); }}
                        readOnly={readOnly}
                        className={readOnly ? 'bg-muted cursor-default' : ''}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company-code">Company Code</Label>
                      <Input
                        id="company-code"
                        value={draft.companyCode}
                        onChange={(e) => { if (!readOnly) updateDraftField('companyCode', e.target.value.toUpperCase()); }}
                        className={`uppercase${readOnly ? ' bg-muted cursor-default' : ''}`}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <div>
                        <Label htmlFor="company-status" className="text-sm font-medium">Active</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
                      </div>
                      <Switch
                        id="company-status"
                        checked={draft.isActive}
                        onCheckedChange={(checked) => { if (!readOnly) updateDraftField('isActive', checked); }}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ── Tab: Users ── */}
              <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 py-5 pb-8">
                  <UsersWithAccessSection company={activeCompany} onUserCountChange={handleUserCountChange} />
                </div>
              </TabsContent>

              {/* ── Sticky footer — single Save All Changes button ── */}
              {!readOnly && (
                <div className="border-t px-6 py-3 shrink-0 flex items-center justify-between gap-3 bg-background">
                  {isDirty ? (
                    <p className="text-xs text-muted-foreground">Unsaved changes</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">All changes saved</p>
                  )}
                  <Button
                    onClick={handleSaveAll}
                    disabled={isSaving || !isDirty}
                    size="sm"
                    className="gap-1.5"
                  >
                    {isSaving ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</>
                    ) : (
                      <><Save className="h-3.5 w-3.5" />Save All Changes</>
                    )}
                  </Button>
                </div>
              )}
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Discard changes confirmation */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this company. Closing now will discard them permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardDialog(false)}>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
